import type {
  LeadAppeal,
  LeadBooking,
  LeadDeal,
  LeadInterest,
  RichLead,
} from '../../lead.popup/types';
import type { Deal, LeadListItem, Opportunity, Unit } from '../../types';
import type { Appeal } from './appealsApi';
import type { Booking } from './bookingApi';
import type { Interest } from './interestApi';
import type { FavoriteUnitRef } from './opportunitiesApi';
import type { RichUnit, UnitInterestedLead } from './unitApi';

/**
 * Normalizes Lead object to be consistent across all APIs
 */
export function fixLead(lead: RichLead | UnitInterestedLead) {
  // reason: working with lead feature may have string ids
  // reason: deal entities referencing leads by string ids
  // reason: search | searchById
  lead.id = String(lead.id);
  // reason: status list api returns strings
  lead.status_id = String(lead.status_id);
  // reason: /api/fe/ad_source/list/local
  lead.ad_source_id =
    lead.ad_source_id === null ? null : String(lead.ad_source_id);

  // all user-related ids should be string because of /api/user/all
  lead.created_by = lead.created_by ? String(lead.created_by) : lead.created_by;
  lead.updated_by = lead.updated_by ? String(lead.updated_by) : lead.updated_by;
  lead.status_last_updated_by = lead.status_last_updated_by
    ? String(lead.status_last_updated_by)
    : lead.status_last_updated_by;
}

/**
 * Normalizes Lead object to be consistent across all APIs
 */
export function fixLeadList(lead: LeadListItem) {
  // reason: working with lead feature may have string ids
  // reason: deal entities referencing leads by string ids
  // reason: search | searchById
  lead.id = String(lead.id);

  // all user-related ids should be string because of /api/user/all
  lead.updated_by_id = String(lead.updated_by_id);

  if (lead.created_by_id) {
    lead.created_by_id = String(lead.created_by_id);
  }
}

/**
 * Normalizes Unit object to be consistent across all APIs
 */
export function fixUnit(unit: RichUnit | Unit) {
  // reason waiting till all entities will have consistent unit_id reference
  unit.unit_id = String(unit.unit_id);
  // reason: status list api returns strings
  unit.status_id = String(unit.status_id);
  // reason: we are blocked by old /unit APIs we need to change them
  // and after that migrate to new enum values
  unit.type = String(unit.type) as typeof unit.type;
  // reason: our section entity still has string id.
  // It conflicts even with sections field in current response
  unit.section_id = String(unit.section_id);
  // reason: waiting till unit info popup will switch to new format too
  // @ts-expect-error
  unit.is_in_resale = String(unit.is_in_resale);
  // reason: waiting till unit info popup will switch to new format too
  // @ts-expect-error
  unit.is_price_per_meter =
    typeof unit.is_price_per_meter === 'number'
      ? String(unit.is_price_per_meter)
      : unit.is_price_per_meter;
}

export function fixSelectedUnits(item: FavoriteUnitRef) {
  // reason waiting till all entities will have consistent unit_id reference
  item.id = String(item.id);
}

/**
 * Normalizes Interest object to be consistent across all APIs
 */
export function fixInterest(interest: Interest | LeadInterest) {
  // reason: needed to retrieve a user
  interest.created_by = String(interest.created_by);
  // reason: Unit entity still has old format
  interest.unit_id = String(interest.unit_id);
}

/**
 * Normalizes Deal object to be consistent across all APIs
 */
export function fixDeal(deal: Deal | LeadDeal) {
  // reason: needed to retrieve a user
  deal.created_by = String(deal.created_by);

  if (deal.opportunity_id) {
    // for some reason backend returns this id for a new entity but converted to string
    deal.opportunity_id = Number(deal.opportunity_id);
  }
}

/**
 * Normalizes Booking object to be consistent across all APIs
 */
export function fixBooking(booking: Booking | LeadBooking) {
  // reason: needed to retrieve a user
  booking.created_by = String(booking.created_by);
  booking.unit_id = String(booking.unit_id);
}

/**
 * Normalizes Appeal object to be consistent across all APIs
 */
export function fixAppeal(appeal: Appeal | LeadAppeal) {
  // AdSource entity has string id
  appeal.ad_source_id =
    appeal.ad_source_id === null ? null : String(appeal.ad_source_id);

  // for lead fields see leadApi for reason explanations
  if (appeal.lead_id) {
    appeal.lead_id = String(appeal.lead_id);
  }

  if (appeal.lead_status_on_create) {
    appeal.lead_status_on_create = String(appeal.lead_status_on_create);
  }

  if (appeal.lead_status_on_close) {
    appeal.lead_status_on_close = String(appeal.lead_status_on_close);
  }

  // reason: /api/object/all
  if (appeal.object_id) {
    appeal.object_id = String(appeal.object_id);
  }

  // reason: Unit entity still has old format
  if (appeal.unit_id) {
    appeal.unit_id = String(appeal.unit_id);
  }

  // all fields related to userId should be string because of userApi
  if (appeal.created_by) {
    // reason: needed to retrieve a user
    appeal.created_by = String(appeal.created_by);
  }

  if (appeal.underway_by) {
    // reason: needed to retrieve a user
    appeal.underway_by = String(appeal.underway_by);
  }

  if (appeal.last_action_by) {
    // reason: needed to retrieve a user
    appeal.last_action_by = String(appeal.last_action_by);
  }

  if (appeal.closed_by) {
    // reason: needed to retrieve a user
    appeal.closed_by = String(appeal.closed_by);
  }
}

/**
 * Normalizes Opportunity object to be consistent across all APIs
 */
export function fixOpportunity(opportunity: Opportunity) {
  // reason: should match LeadListItem id type requirement
  opportunity.lead_id = String(opportunity.lead_id);
  // reason: needed to retrieve a user
  opportunity.manager_id = String(opportunity.manager_id);
  // reason: needed to retrieve a user
  opportunity.created_by = String(opportunity.created_by);
  // reason: needed to retrieve a user
  opportunity.updated_by = String(opportunity.updated_by);

  if (opportunity.closed_by) {
    // reason: needed to retrieve a user
    opportunity.closed_by = String(opportunity.closed_by);
  }
}
